<template>
    <div>
        <configs-content :loading="$wait.is('fetching.account')">
            <h2 class="flex items-center text-3xl mb-4">
                <nio-icon class="mr-2" name="regular/office-outdoors" />
                {{ $t('configs.rightPanel.teamDetails.subtitles.teamDetails') }}
            </h2>

            <div class="space-y-8">
                <section class="space-y-6">
                    <div class="flex space-x-6">
                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.name')">
                            <nio-input :required="true" v-model="account.name" />
                        </nio-input-group>
                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.phone')">
                            <nio-input mask="phone" v-model="account.phone" />
                        </nio-input-group>
                    </div>

                    <div class="flex space-x-6">
                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.timezone')">
                            <nio-select :options="timezones" v-model="account.timezone" />
                        </nio-input-group>

                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.country')">
                            <nio-select :options="countries" v-model="account.country" />
                        </nio-input-group>
                    </div>

                    <div class="flex space-x-6">
                        <nio-input-group class="flex-1" :label="!isUnitedStatesAccount ? $t('configs.rightPanel.teamDetails.province') : $t('configs.rightPanel.teamDetails.state')">
                            <nio-select :options="provincesOrStates" v-model="account.province" />
                        </nio-input-group>

                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.address')">
                            <nio-input v-model="account.address" />
                        </nio-input-group>
                    </div>

                    <div class="flex space-x-6">
                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.city')">
                            <nio-input v-model="account.city" />
                        </nio-input-group>

                        <nio-input-group class="flex-1" :label="isUnitedStatesAccount ? $t('configs.rightPanel.teamDetails.zip') : $t('configs.rightPanel.teamDetails.postalCode')">
                            <nio-input :mask="isUnitedStatesAccount ? '#####' : 'A#A #A#'" v-model="account.postal_code" />
                        </nio-input-group>
                    </div>
                </section>

                <section>
                    <h2 class="flex items-center text-xl mb-4">
                        <nio-icon class="mr-2" name="regular/module-three-1" />
                        {{ $t('configs.rightPanel.teamDetails.subtitles.modules') }}
                    </h2>
                    <div class="flex space-x-6">
                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.appointmentAccess')">
                            <nio-toggle :disabled="true" :value="account.appointment_module">
                                <span v-text="appointmentModuleText" />
                            </nio-toggle>
                        </nio-input-group>
                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.campaignAccess')">
                            <nio-toggle :disabled="true" :value="account.campaign_module">
                                <span v-text="campaignModuleText" />
                            </nio-toggle>
                        </nio-input-group>
                        <nio-input-group class="flex-1" :label="$t('configs.rightPanel.teamDetails.paymentAccess')">
                            <nio-toggle :disabled="true" :value="account.payment_module">
                                <span v-text="paymentModuleText" />
                            </nio-toggle>
                        </nio-input-group>
                    </div>
                </section>

                <section>
                    <h2 class="flex items-center text-xl mb-4">
                        <nio-icon class="mr-2" name="regular/phone-actions-call" />
                        {{ $t('configs.rightPanel.teamDetails.subtitles.phones') }}
                    </h2>
                    <div class="flex flex-col w-full border border-gray-200 rounded">
                        <div class="flex items-center p-4 border-b border-gray-200 rounded-t bg-gray-100">
                            <div class="flex-2">
                                {{ $t('configs.rightPanel.teamDetails.number') }}
                            </div>
                            <div class="flex-1 flex justify-end">
                                &nbsp;
                            </div>
                        </div>

                        <div class="flex flex-col items-center w-full h-full">
                            <div class="flex justify-center items-center p-4 w-full h-full" v-if="!phones.length">
                                {{ $t('configs.rightPanel.teamDetails.noPhones') }}
                            </div>

                            <div
                                class="flex items-center p-4 w-full"
                                :key="phone.id"
                                v-for="phone in phones"
                            >
                                <div class="flex-1">
                                    {{ formatPhone(phone.number) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <error-message />
            </div>
        </configs-content>

        <configs-footer>
            <nio-button
                color="blue"
                :loading="$wait.is('updating.account')"
                @click="update"
            >
                {{ $t('global.general.save') }}
            </nio-button>
        </configs-footer>
    </div>
</template>

<script>
    import { mapActions, mapGetters, mapState } from 'vuex';
    import { merge } from '@/utils/index.js';
    import ConfigsContent from '@/components/ConfigsContent.vue';
    import ConfigsFooter from '@/components/ConfigsFooter.vue';

    const defaultAccount = {
        id: null,
        name: '',
        phone: '',
        timezone: '',
        appointment_module: false,
        campaign_module: false,
        payment_module: false,
        address: '',
        city: '',
        postal_code: '',
        province: '',
        country: '',
    };

    export default {
        components: {
            ConfigsContent,
            ConfigsFooter,
        },

        data() {
            return {
                account: { ...defaultAccount },
                search: '',
            };
        },

        computed: {
            ...mapGetters([
                'timezones',
                'countries',
                'provinces',
                'states',
            ]),

            ...mapState([
                'authUser',
            ]),

            ...mapState('setting', [
                'phones',
            ]),

            paymentModuleText() {
                return this.account.payment_module
                    ? this.$t('configs.rightPanel.teamDetails.hasPayments')
                    : this.$t('configs.rightPanel.teamDetails.doesNotHavePayments');
            },

            campaignModuleText() {
                return this.account.campaign_module
                    ? this.$t('configs.rightPanel.teamDetails.hasCampaigns')
                    : this.$t('configs.rightPanel.teamDetails.doesNotHaveCampaigns');
            },

            appointmentModuleText() {
                return this.account.appointment_module
                    ? this.$t('configs.rightPanel.teamDetails.hasAppointments')
                    : this.$t('configs.rightPanel.teamDetails.doesNotHaveAppointments');
            },

            isUnitedStatesAccount() {
                return this.account.country === 'US';
            },

            provincesOrStates() {
                return !this.isUnitedStatesAccount ? this.provinces : this.states;
            },
        },

        watch: {
            'authUser.account.id': {
                immediate: true,
                handler(newValue) {
                    if (!newValue) {
                        return;
                    }

                    this.fetch();
                },
            },

            'account.country'() {
                if (this.$wait.is('fetching.account')) {
                    return;
                }

                this.account.province = null;
                this.account.postal_code = null;
            },
        },

        methods: {
            ...mapActions('setting', [
                'updateAccount',
                'fetchAccount',
                'fetchAccountPhones',
            ]),

            async update() {
                this.$wait.start('updating.account');

                try {
                    await this.updateAccount(this.account);

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('configs.rightPanel.teamDetails.messages.updateSuccessful'),
                    });

                    this.$wait.end('updating.account');
                } catch (error) {
                    this.$wait.end('updating.account');

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('configs.rightPanel.teamDetails.errors.update'),
                    });

                    throw error;
                }
            },

            async fetch() {
                this.$wait.start('fetching.account');

                try {
                    const account = await this.fetchAccount();

                    merge(this.account, account);
                } catch (error) {
                    this.$wait.end('fetching.account');

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('configs.rightPanel.teamDetails.errors.fetchingAccount'),
                    });

                    throw error;
                }

                this.account.phone = this.account.phone ? this.account.phone.substr(1) : '';

                try {
                    await this.fetchAccountPhones();
                } catch (error) {
                    this.$wait.end('fetching.account');

                    this.$notify.show({
                        type: 'error',
                        message: this.$t('configs.rightPanel.teamDetails.errors.fetchingPhones'),
                    });

                    throw error;
                }

                this.$wait.end('fetching.account');
            },
        },
    };
</script>
